import { makeAutoObservable } from 'mobx'

export const PRIORITY_TAB_IDS = [
	'essays',
	'feedback',
	'documents',
	'recommendations',
	'assignments',
]

const createTabState = () => ({
	items: [],
	total: 0,
	loadStatus: 'idle',
	fetchedOnce: false,
	lastSyncAt: null,
})

const GET_ID_BY_TAB = {
	essays: (item) => String(item?.id || item?.versionId || '').trim(),
	feedback: (item) => String(item?.id || item?.versionId || '').trim(),
	documents: (item) => String(item?.id || item?.studentId || '').trim(),
	recommendations: (item) =>
		String(
			item?.id ||
				(item?.studentId && item?.applicationTargetId
					? `${item.studentId}:${item.applicationTargetId}`
					: item?.studentId) ||
				'',
		).trim(),
	assignments: (task) =>
		`${String(task?.task_id || '').trim()}:${String(task?.studentId || '').trim()}`,
}

function mergeById(prev, next, getId) {
	const nextMap = new Map()
	const nextWithoutId = []
	for (const row of next) {
		const id = getId(row)
		if (id) nextMap.set(id, row)
		else nextWithoutId.push(row)
	}
	const kept = []
	const seen = new Set()
	for (const old of prev) {
		const id = getId(old)
		if (!id || !nextMap.has(id)) continue
		kept.push(nextMap.get(id))
		seen.add(id)
	}
	const fresh = next.filter(row => {
		const id = getId(row)
		return id && !seen.has(id)
	})
	return [...fresh, ...kept, ...nextWithoutId]
}

class PriorityTabsStore {
	tabs = {
		essays: createTabState(),
		feedback: createTabState(),
		documents: createTabState(),
		recommendations: createTabState(),
		assignments: createTabState(),
	}

	suppressedIds = {
		essays: new Set(),
		feedback: new Set(),
		documents: new Set(),
		recommendations: new Set(),
		assignments: new Set(),
	}

	constructor() {
		makeAutoObservable(this, {}, { autoBind: true })
	}

	_getSuppressed(tabId) {
		if (!this.suppressedIds[tabId]) {
			this.suppressedIds[tabId] = new Set()
		}
		return this.suppressedIds[tabId]
	}

	_applySuppressed(tabId, items) {
		const getId = GET_ID_BY_TAB[tabId] || ((x) => String(x?.id || ''))
		const suppressed = this._getSuppressed(tabId)
		const nextItems = Array.isArray(items) ? items : []
		const nextIds = new Set(
			nextItems.map(row => getId(row)).filter(Boolean),
		)

		// Server still lists the item as pending → drop stale local suppression.
		for (const id of [...suppressed]) {
			if (nextIds.has(id)) {
				suppressed.delete(id)
			}
		}

		return nextItems.filter(row => {
			const id = getId(row)
			return !id || !suppressed.has(id)
		})
	}

	getTab(tabId) {
		return this.tabs[tabId] || createTabState()
	}

	beginLoad(tabId) {
		const tab = this.getTab(tabId)
		if (!tab.fetchedOnce || tab.items.length === 0) {
			tab.loadStatus = 'loading'
		} else {
			tab.loadStatus = 'refreshing'
		}
	}

	setItems(tabId, items, total) {
		const tab = this.getTab(tabId)
		const getId = GET_ID_BY_TAB[tabId] || ((x) => String(x?.id || ''))
		const nextItems = this._applySuppressed(
			tabId,
			Array.isArray(items) ? items : [],
		)
		tab.items = tab.fetchedOnce
			? mergeById(tab.items, nextItems, getId)
			: nextItems
		tab.total = Math.max(
			tab.items.length,
			Math.max(0, (Number(total) || tab.items.length) - this._getSuppressed(tabId).size),
		)
		tab.fetchedOnce = true
		tab.loadStatus = 'success'
		tab.lastSyncAt = Date.now()
	}

	setError(tabId) {
		const tab = this.getTab(tabId)
		if (tab.items.length === 0) {
			tab.loadStatus = 'error'
		} else {
			tab.loadStatus = 'success'
		}
	}

	removeById(tabId, refId) {
		const tab = this.getTab(tabId)
		const id = String(refId || '').trim()
		if (!id) return
		const getId = GET_ID_BY_TAB[tabId]
		if (!getId) return
		this._getSuppressed(tabId).add(id)
		const before = tab.items.length
		tab.items = tab.items.filter((row) => getId(row) !== id)
		if (tab.items.length !== before) {
			tab.total = Math.max(0, tab.total - 1)
		}
	}

	removeAssignmentById(assignmentId) {
		const tab = this.getTab('assignments')
		const id = String(assignmentId || '').trim()
		if (!id) return
		const before = tab.items.length
		tab.items = tab.items.filter(
			(task) => String(task?.task_id || '').trim() !== id,
		)
		if (tab.items.length !== before) {
			tab.total = Math.max(0, Math.min(tab.total, tab.items.length))
		}
	}

	getPollingSnapshot(tabId) {
		const tab = this.getTab(tabId)
		const getId = GET_ID_BY_TAB[tabId] || ((x) => String(x?.id || ''))
		return tab.items.map((row) => ({
			id: getId(row),
			updatedAt:
				row?.submittedAt ||
				row?.lastUpdatedAt ||
				row?.submission_date ||
				row?.completed_at ||
				row?.previewMessage?.createdAt ||
				null,
		}))
	}
}

export const priorityTabsStore = new PriorityTabsStore()
